import { Drawer, type DrawerProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  type TypographyVariant,
} from 'hephaestus/web';
import { Close } from 'hephaestus/webIcons';
import { merge } from 'lodash-es';
import type { ElementType, ReactNode } from 'react';
import { SideDrawerSize, useSideDrawerTokens } from './sideDrawer.tokens';

type SideDrawerPassthroughProps = Omit<
  DrawerProps,
  'open' | 'anchor' | 'onClose' | 'children' | 'title'
>;

export type SideDrawerProps = SideDrawerPassthroughProps & {
  title: ReactNode;
  /** Root element for the title typography (e.g. `'h3'` for drawer headings). */
  titleComponent?: ElementType;
  /** Title typography variant (default matches legacy SideDrawer styling). */
  titleVariant?: TypographyVariant;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Merged after default body layout tokens (e.g. `px: 0` for edge-to-edge children). */
  bodySx?: SxProps<Theme>;
  /** Merged after default footer tokens (e.g. `p: 0` when padding is applied per row inside `footer`). */
  footerSx?: SxProps<Theme>;
  /** Fixed width preset (`xs` remains full width). Default matches the legacy 480px drawer. */
  size?: SideDrawerSize;
  hideBackdrop?: boolean;
  closeOnOutsideClick?: boolean;
  testIds?: { closeButton?: string };
};

export const SideDrawer = (props: SideDrawerProps): JSX.Element => {
  const {
    title,
    titleComponent = 'div',
    titleVariant = 'headingSmall',
    isOpen,
    onClose,
    children,
    footer,
    bodySx,
    footerSx,
    size = SideDrawerSize.Default,
    hideBackdrop = true,
    closeOnOutsideClick = false,
    testIds,
    sx,
    slotProps,
    ModalProps,
    BackdropProps,
    ...restDrawerProps
  } = props;

  const { sideDrawer: sideDrawerTokens } = useSideDrawerTokens(size);

  const effectiveHideBackdrop = closeOnOutsideClick
    ? false
    : (ModalProps?.hideBackdrop ?? hideBackdrop);

  return (
    <Drawer
      anchor='right'
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      slotProps={slotProps}
      BackdropProps={{
        ...(closeOnOutsideClick ? { invisible: true } : {}),
        ...BackdropProps,
      }}
      ModalProps={{
        ...ModalProps,
        hideBackdrop: effectiveHideBackdrop,
      }}
      sx={merge({}, sideDrawerTokens.drawerRootSx, sx ?? {}) as any}
      {...restDrawerProps}
    >
      <Box sx={sideDrawerTokens.headerSx as any}>
        <Typography component={titleComponent} variant={titleVariant}>
          {title}
        </Typography>
        <Tooltip title='Close'>
          <IconButton
            aria-label='Close'
            onClick={onClose}
            data-testid={testIds?.closeButton}
            size='xsmall'
          >
            <Close fontSize='inherit' />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={merge({}, sideDrawerTokens.bodySx, bodySx ?? {}) as any}>
        {children}
      </Box>
      {footer && (
        <Box sx={merge({}, sideDrawerTokens.footerSx, footerSx ?? {}) as any}>
          {footer}
        </Box>
      )}
    </Drawer>
  );
};
